import { Link } from "react-router";
import { Icon } from "@/components/icons";
import { formatDate, formatNumber } from "@/app/(app)/_lib/format";
import { StatCard } from "./stat-card";
import { SCREENING_REQUESTS_30D, SCREENING_REQUESTS_12M } from "../_data";
import type { ScreeningCounts } from "@/src/services/dashboard_counts_service";
import type { LastAlert, MonitoringKpi } from "@/src/services/monitoring_service";

export const SCREENING_REQUESTS_INFO = "Counting may include requests in trial period";

/**
 * `YYYY-MM-DD HH:mm` (local, 24h) for the exact datetime; `YYYY-MM-DD` for a day
 * bucket and `YYYY-MM` for a month bucket, which carry no time. The exact
 * datetime goes through a Date so it lands on the viewer's local day and hour.
 */
function formatLastAlert(last: LastAlert): string | null {
  if (last.period === "month") return last.month || null;
  if (last.period === "day") {
    const label = formatDate(last.date);
    return label === "—" ? null : label;
  }
  const at = new Date(last.at);
  if (Number.isNaN(at.getTime())) return null;
  const hh = String(at.getHours()).padStart(2, "0");
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `${formatDate(at)} ${hh}:${mm}`;
}

export function OpenAlertsCard({
  empty,
  count,
  lastAlert,
}: {
  empty: boolean;
  /** Null on the mock/preview path. */
  count: number | null;
  lastAlert: LastAlert | null;
}) {
  const live = count !== null;
  const totalNew = count ?? 0;
  const isEmpty = empty || (live && totalNew === 0);
  const lastAlertLabel = (() => {
    if (!lastAlert) return null;
    const label = formatLastAlert(lastAlert);
    return label ? `Last alert: ${label}` : null;
  })();
  return (
    <StatCard
      icon="error"
      iconTone={isEmpty ? "neutral" : "negative"}
      pill={isEmpty ? undefined : { label: "Requires action", tone: "negative" }}
      label="Open monitoring alerts"
      value={isEmpty ? "0" : formatNumber(totalNew)}
      footerLeft={
        <span className="text-tiny text-grey-600">
          {!isEmpty && lastAlertLabel ? lastAlertLabel : "No alerts yet"}
        </span>
      }
      footerRight={
        isEmpty ? undefined : (
          <Link
            to="/monitoring-portal"
            className="inline-flex items-center py-3.5 -my-3.5 text-tiny font-medium uppercase tracking-[0.04em] text-green-700 hover:text-green-600"
          >
            Review all
          </Link>
        )
      }
    />
  );
}

export function MonitoredNamesCard({
  empty,
  kpi,
}: {
  empty: boolean;
  /** Account-wide totals; null on the mock/preview path. */
  kpi: MonitoringKpi | null;
}) {
  const live = kpi !== null;
  const total = kpi?.total ?? 0;
  const matched = kpi?.matched ?? 0;
  const isEmpty = empty || (live && total === 0);
  return (
    <StatCard
      icon="radar"
      iconTone="neutral"
      label="Monitored names"
      value={isEmpty ? "0" : formatNumber(total)}
      footerLeft={
        isEmpty ? undefined : (
          <span className="text-tiny text-grey-600">{formatNumber(matched)} with matches</span>
        )
      }
      footerRight={
        isEmpty ? undefined : (
          <Link
            to="/monitoring-portal"
            className="inline-flex items-center py-3.5 -my-3.5 text-tiny font-medium uppercase tracking-[0.04em] text-green-700 hover:text-green-600"
          >
            View list
          </Link>
        )
      }
    />
  );
}

function TrendFooter({ pct, window }: { pct: string; window: string }) {
  const down = parseFloat(pct) < 0;
  return (
    <span
      className={`flex items-center gap-1 text-tiny font-medium ${down ? "text-grey-600" : "text-green-700"}`}
    >
      <Icon name={down ? "trending_down" : "trending_up"} size={20} />
      {pct}% vs previous {window}
    </span>
  );
}

export function ScreeningRequests30dCard({
  empty,
  counts,
}: {
  empty: boolean;
  counts: ScreeningCounts | null;
}) {
  const total = counts ? counts.total30d : SCREENING_REQUESTS_30D.reduce((a, b) => a + b, 0);
  const pct = counts ? counts.pct30d : "12";
  const isEmpty = empty || (counts ? counts.total30d === 0 : false);
  return (
    <StatCard
      icon="search"
      iconTone="neutral"
      pill={{ label: "Last 30 days", tone: "neutral" }}
      label="Screening requests"
      labelInfo={SCREENING_REQUESTS_INFO}
      value={isEmpty ? "0" : formatNumber(total)}
      footerLeft={
        isEmpty ? (
          <span className="text-tiny text-grey-600">No requests yet</span>
        ) : (
          <TrendFooter pct={pct} window="30 days" />
        )
      }
    />
  );
}

export function ScreeningRequests12mCard({
  empty,
  counts,
}: {
  empty: boolean;
  counts: ScreeningCounts | null;
}) {
  const total = counts ? counts.total12m : SCREENING_REQUESTS_12M.reduce((a, b) => a + b, 0);
  const pct = counts ? counts.pct12m : "70.3";
  const isEmpty = empty || (counts ? counts.total12m === 0 : false);
  return (
    <StatCard
      icon="calendar_month"
      iconTone="neutral"
      pill={{ label: "Last 12 months", tone: "neutral" }}
      label="Screening requests"
      labelInfo={SCREENING_REQUESTS_INFO}
      value={isEmpty ? "0" : formatNumber(total)}
      footerLeft={
        isEmpty ? (
          <span className="text-tiny text-grey-600">No requests yet</span>
        ) : (
          <TrendFooter pct={pct} window="12 months" />
        )
      }
    />
  );
}
